import React, { useState } from 'react';
import { Box, Typography, Button, Theme } from '@material-ui/core';
import { Edit, Delete, Close, ExpandMore, ChevronRight, FileCopy, Link as LinkIcon } from '@material-ui/icons';
import { makeStyles, createStyles } from '@material-ui/styles';
import { useHistory } from 'react-router';
import { TabletButton } from '@campfire/tablet-button';
import { unpackToDateTime } from '@campfire/hot-date';
import Icon from '@mdi/react';
import { mdiQrcode } from '@mdi/js';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../global/config/useSnackbar';
import { useUser } from '../../../../global/auth/useUser';
import { DeleteFormDialog } from './DeleteFormDialog';
import { TooltipMenu } from '../../../../common/tooltip-menu';
import { FormQRCodePrintPage } from './FormQRCodePrintPage';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: '40px',
    },
    field: {
      '& .header': {
        display: 'flex',
        borderBottom: `1px solid ${theme.color.grey.neutralBrand200}`,
        paddingBottom: '12px',
        alignItems: 'center',
      },
      '& .title': {
        fontWeight: 700,
        fontSize: '18px',
        color: theme.color.grey.neutralBrand800,
        flex: 1,
      },
      '& .subTitle': {
        fontWeight: 300,
        fontSize: '16px',
        color: theme.color.grey.neutralBrand500,
        marginTop: '20px',
      },
      '& .button': {
        textTransform: 'none',
        fontSize: '14px',
        fontWeight: 600,
        padding: '8px',
        minWidth: 'unset',
        minHeight: 'unset',
        height: '30px',
      },
      '& .buttonGroup': {
        display: 'flex',
        gap: '12px',
      },
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '6px 12px',
      borderRadius: '4px',
      fontSize: '0.75rem',
      fontWeight: 550,
      marginRight: '8px',
      marginBottom: '8px',
    },
    optionItem: {
      color: theme.alert.blue.extraLight,
      display: 'flex',
      alignItems: 'center',
      paddingTop: '10px',
      justifyContent: 'center',
      '& p': {
        fontSize: '14px',
      },
      '& svg': {
        fontSize: '16px',
      },
      textTransform: 'none',
    },
  })
);

interface FormDetailsProps {
  form: any;
  orgLogo?: string | null;
  onClose?: () => void;
  onRefetch: () => void;
}

export const FormDetails: React.FC<FormDetailsProps> = ({ form, orgLogo, onClose, onRefetch }) => {
  const classes = useStyles();
  const { theme } = useCampfireTheme();
  const history = useHistory();
  const apiUrl = useApiUrl();
  const { setSnackbar } = useSnackbar();
  const { getManagerIdentity } = useUser();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const deleteForm = useCampfireFetch({ defer: true });
  const duplicateForm = useCampfireFetch({ defer: true });

  const handleEdit = () => {
    history.push(`/admin/forms/form-builder?fid=${form.formId}`);
  };

  const handleDelete = () => {
    setOpenDeleteDialog(true);
    setAnchorEl(null);
  };

  const handleDuplicate = async () => {
    setAnchorEl(null);

    try {
      const res = await duplicateForm.run({
        url: `${apiUrl}/vm/form/duplicate`,
        method: 'post',
        data: {
          formId: form.formId,
          managerId: getManagerIdentity().managerId,
        },
      });

      if (!res.ok) {
        setSnackbar({
          open: true,
          message: res.data?.message || 'Unable to duplicate form',
          variant: 'error',
        });
        return;
      }

      setSnackbar({
        open: true,
        message: 'Form duplicated',
        variant: 'success',
      });

      onRefetch();
      history.push(`/admin/forms/form-builder?fid=${res.data.data.formId}`);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Unable to duplicate form',
        variant: 'error',
      });
    }
  };

  const handleConfirmDelete = async () => {
    try {
      const res = await deleteForm.run({
        url: `${apiUrl}/vm/form/delete`,
        method: 'post',
        data: {
          formId: form.formId,
        },
      });

      if (!res.ok) {
        setSnackbar({
          open: true,
          message: 'Unable to delete form',
          variant: 'error',
        });
        return;
      }

      setOpenDeleteDialog(false);
      setSnackbar({
        open: true,
        message: 'Form deleted successfully',
        variant: 'success',
      });

      if (onClose) onClose();
      onRefetch();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Unable to delete form',
        variant: 'error',
      });
    }
  };

  const onOption = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onOptionClose = () => setAnchorEl(null);

  const handleCopyLink = () => {
    const formUrl = `${window.location.origin}/form/${form.formId}`;
    navigator.clipboard.writeText(formUrl).then(() => {
      setSnackbar({
        open: true,
        message: 'Link copied to clipboard',
        variant: 'success',
      });
    });
    setAnchorEl(null);
  };

  const isClosed = form.dateClosed && new Date(form.dateClosed) <= new Date();

  return (
    <Box className={classes.container}>
      <Box className={classes.field}>
        <Box className='header'>
          <Typography className='title'>{form.title}</Typography>
          <Box className='buttonGroup'>
            <TabletButton
              size='small'
              variant='outlined'
              color='primary'
              onClick={handleEdit}
              startIcon={<Edit />}
              style={{
                border: '1px solid #9e9e9e',
                boxSizing: 'border-box',
                borderRadius: '4px',
                padding: '2px 7px',
                color: theme.color.grey.neutral300,
                marginTop: '5px',
              }}
            >
              {'Edit'}
            </TabletButton>
            <Box display='flex'>
              <TooltipMenu
                open={Boolean(anchorEl)}
                onClose={onOptionClose}
                title={
                  <Box display='flex' flexDirection='column' alignItems='flex-start' style={{ padding: '0 5px' }}>
                    {!isClosed && (
                      <>
                        <Button className={classes.optionItem} startIcon={<LinkIcon />} onClick={handleCopyLink}>
                          Copy Link
                        </Button>
                        <FormQRCodePrintPage
                          formId={form.formId}
                          formTitle={form.title}
                          orgLogo={orgLogo}
                          trigger={() => (
                            <Button
                              className={classes.optionItem}
                              startIcon={<Icon path={mdiQrcode} size={0.7} />}
                              onClick={() => setAnchorEl(null)}
                            >
                              QR Code
                            </Button>
                          )}
                        />
                      </>
                    )}
                    <Button
                      className={classes.optionItem}
                      startIcon={<FileCopy />}
                      onClick={handleDuplicate}
                      disabled={duplicateForm.loading}
                    >
                      Duplicate
                    </Button>
                    <Button
                      className={classes.optionItem}
                      style={{ color: '#E5B3B3' }}
                      startIcon={<Delete />}
                      onClick={handleDelete}
                    >
                      Delete
                    </Button>
                  </Box>
                }
              >
                <TabletButton
                  size='small'
                  variant='outlined'
                  color='primary'
                  aria-controls='form-options-menu'
                  aria-haspopup='true'
                  onClick={(e) => onOption(e)}
                  endIcon={<ExpandMore />}
                  style={{
                    border: '1px solid #9e9e9e',
                    boxSizing: 'border-box',
                    borderRadius: '4px',
                    padding: '2px 7px',
                    color: theme.color.grey.neutral300,
                    marginTop: '5px',
                  }}
                >
                  {'Options'}
                </TabletButton>
              </TooltipMenu>
              {onClose && (
                <TabletButton
                  size='small'
                  variant='outlined'
                  onClick={() => onClose()}
                  style={{
                    border: '1px solid #ff5252',
                    boxSizing: 'border-box',
                    borderRadius: '4px',
                    padding: '4px',
                    minWidth: '32px',
                    color: '#ff5252',
                    marginTop: '5px',
                    marginLeft: '8px',
                  }}
                >
                  <Close style={{ fontSize: '18px' }} />
                </TabletButton>
              )}
            </Box>
          </Box>
        </Box>
        <Typography className={'subTitle'}>{form.description}</Typography>
      </Box>

      <Box className={classes.field}>
        <Box className='header' style={{ marginBottom: '20px' }}>
          <Typography className='title'>Status</Typography>
        </Box>
        <Box>
          {form.isListed ? (
            <span
              className={classes.badge}
              style={{
                backgroundColor: `${theme.alert.green.light}20`,
                color: theme.alert.green.dark,
              }}
            >
              Public
            </span>
          ) : (
            <span
              className={classes.badge}
              style={{
                backgroundColor: `${theme.alert.yellow.light}20`,
                color: theme.alert.yellow.dark,
              }}
            >
              Private
            </span>
          )}
          {isClosed ? (
            <span
              className={classes.badge}
              style={{
                backgroundColor: `${theme.alert.red.light}20`,
                color: theme.alert.red.dark,
              }}
            >
              Closed
            </span>
          ) : (
            <span
              className={classes.badge}
              style={{
                backgroundColor: `${theme.alert.blue.light}20`,
                color: theme.alert.blue.dark,
              }}
            >
              Open
            </span>
          )}
        </Box>
      </Box>

      <Box className={classes.field}>
        <Box className='header'>
          <Typography className='title'>Responses</Typography>
          <TabletButton
            size='small'
            variant='outlined'
            color='primary'
            endIcon={<ChevronRight />}
            onClick={() => history.push(`/admin/forms/responses?fid=${form.formId}`)}
            style={{
              border: '1px solid #9e9e9e',
              boxSizing: 'border-box',
              borderRadius: '4px',
              padding: '2px 7px',
              color: theme.color.grey.neutral300,
              marginTop: '5px',
            }}
          >
            View
          </TabletButton>
        </Box>
        <Typography className={'subTitle'} style={{ marginTop: '10px' }}>
          {form.submissionStats?.totalSubmissions || 0}{' '}
          {form.submissionStats?.totalSubmissions === 1 ? 'response' : 'responses'}
        </Typography>
      </Box>

      <Box className={classes.field}>
        <Box className='header'>
          <Typography className='title'>Details</Typography>
        </Box>
        <Typography className={'subTitle'} style={{ marginTop: '10px' }}>
          Created on {unpackToDateTime(form.dateCreated).toFormat('d LLL yyyy')}
          {form.manager ? ` by ${form.manager.profile.preferredName}` : ''}
        </Typography>
        {form.successMessage && (
          <Box style={{ marginTop: '20px' }}>
            <Typography style={{ fontWeight: 600, fontSize: '14px', color: theme.color.grey.neutralBrand800 }}>
              Success Message
            </Typography>
            <Typography style={{ fontSize: '14px', color: theme.color.grey.neutralBrand500, marginTop: '8px' }}>
              {form.successMessage}
            </Typography>
          </Box>
        )}
      </Box>

      <DeleteFormDialog
        open={openDeleteDialog}
        formName={form.title}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
};
